import { inject, Inject, Injectable } from '@angular/core';
import { HttpEventType, HttpEvent } from '@angular/common/http';
import { GedConfig, GedPiece, GedDocument, GedData, PiecesByTypeStructure } from '@app/models/ged';
import { ApiCrudService } from './api-crud.service';
import { FamilyService } from './family.service';
import { map } from 'rxjs/operators';
import { AccountType } from '@app/models/user';
import { SnackbarService } from './snackbar.service';
import { CustomHttpService } from './custom-http.service';
import { TranslateService } from '@ngx-translate/core';

const iconClasses = {
  image: ['jpg', 'jpeg', 'png'],
  pdf: ['pdf'],
  word: ['doc', 'docx']
};

@Injectable({
  providedIn: 'root'
})
export class GedService extends ApiCrudService<any> {

  configUrl = 'conf/ged';
  url = 'ged';
  private translateService = inject(TranslateService);

  constructor(
    protected http: CustomHttpService,
    private familyService: FamilyService,
    private snackbarService: SnackbarService
  ) {
    super();
  }

  getConfig(isAmdin = false) {
    if (isAmdin) {
      return this.http.get<GedConfig>(this.configUrl, this.httpOptions);
    }
    return this.http.getWithCache<GedConfig>(this.configUrl, 30, this.httpOptions);
  }

  updateConfig(config: GedConfig | any) {
    return this.http.put(this.configUrl, { config }, this.httpOptions);
  }

  sendDocument(file: File, piece: GedPiece, idEntite: number, typeAccount: AccountType) {

    const formData = new FormData();

    formData.set('file', file);
    formData.set('type', file.type);

    formData.set('entite', piece.obj);
    formData.set('idPieceAFournir', piece.id + '');

    formData.set('idEntite', idEntite + '');

    if (typeAccount === 'family') {
      return this.http.post(`familles/${this.familyService.currentFamily.id}/documents`, formData, {
        reportProgress: true,
        observe: 'events',
      }).pipe(map(event => this.mapProgressResponse(event)));
    }

    if (typeAccount === 'assmat') {
      return this.http.post(`assmats/${idEntite}/documents`, formData, {
        reportProgress: true,
        observe: 'events',
      }).pipe(map(event => this.mapProgressResponse(event)));
    }
  }

  getCurrentFamilyDocuments() {
    return this.http.get<GedDocument[]>(`familles/${this.familyService.currentFamily.id}/documents`);
  }

  getAssmatDocuments(idAssmat: number) {
    return this.http.get<GedDocument[]>(`assmats/${idAssmat}/documents`);
  }

  getDocument(idDocument) {
    // Le service worker d'Angular met en cache toutes les requêtes qui correspondent au pattern 'documents/**'.
    // Pour éviter la mise en cache de requêtes (ex: /documents/-1), on ajoute dynamiquement
    // un paramètre de requête 'noCache' avec une valeur unique, forçant ainsi une requête réseau fraîche.
    const url = idDocument < 0
      ? `documents/${idDocument}?noCache=${Date.now()}`
      : `documents/${idDocument}`;

    return this.http.get(url, {
      responseType: 'blob',
      reportProgress: true,
      observe: 'events'
    }).pipe(map(response => this.mapProgressResponse(response)));
  }

  mapProgressResponse(event: HttpEvent<any>) {
    switch (event.type) {
      case HttpEventType.DownloadProgress:
        return { type: 'dl_progress', value: Math.round(100 * event.loaded / event.total) };
      case HttpEventType.UploadProgress:
        return { type: 'up_progress', value: Math.round(100 * event.loaded / event.total) };
      case HttpEventType.Response:
        return { type: 'response', value: event.body };
      default:
        return { type: 'otherType', value: event.type };
    }
  }

  checkDocuments(fileList: FileList, configGed: GedConfig, uploadingFile: File) {
    const uploadingFileExtension = this.getExtension(fileList[0].name);
    const fileConfigSize = +configGed.size * 1048576;
    const fileConfigExtension = configGed.extensions.toLocaleLowerCase().split(' ');
    let errorMessage: string;

    if (!fileConfigExtension.includes(uploadingFileExtension) && !configGed.extensions.includes('*')) {
      errorMessage = `Ce type de fichier n\'est pas autorisé. Fichiers autorisés : ${configGed.extensions}.`;
    }

    if (uploadingFile.size > fileConfigSize) {
      errorMessage = `Fichier trop volumineux. Le fichier ne doit pas dépasser ${configGed.size}Mo.`;
    }

    return errorMessage
  }

  getExtension(fileName: string) {
    return fileName.split('.').pop().toLowerCase();
  }

  getFileIcon(doc: GedDocument) {
    const extension = doc.nomFichier.split('.').pop().toLowerCase();
    return Object.keys(iconClasses).find(key => iconClasses[key].includes(extension));
  }

  downloadDocument(doc: GedDocument, documentDownload: any) {
    this.getDocument(doc.idDocumentElectronique).subscribe(resp => {
      if (resp.type === 'dl_progress') {
        documentDownload[doc.idDocumentElectronique] = resp.value as number;
      } else if (resp.type === 'response') {
        this.openBlob(doc, resp.value, documentDownload);
      }
    }, err => this.snackbarService.error('Erreur lors de l\'ouverture du fichier : ' + err));
  }

  // @see https://stackoverflow.com/questions/52154874/angular-6-downloading-file-from-rest-api
  // => keep this reference, if needs IE support ...
  openBlob(doc: GedDocument, blob: Blob, documentDownload: any) {
    const blobURL = window.URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = blobURL;
    link.download = doc.nomFichier;

    link.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true, view: window }));

    setTimeout(() => {
      documentDownload[doc.idDocumentElectronique] = null;
      window.URL.revokeObjectURL(blobURL);
    }, 100);
  }

  sortByOrder(array: GedPiece[]) {
    array.sort((a: GedPiece, b: GedPiece) => {
      if (a.order < b.order) return -1;
      if (a.order > b.order) return 1;
      return 0;
    });
  }

  mapListPieceAFournir(entities: string[], listePiecesAFournir: GedPiece[]): { [entite: string]: GedData } {
    let piecesAFournirByEntite: { [entite: string]: GedData } = {};

    entities.forEach(entite => {
      const pieces = listePiecesAFournir.filter(piece => piece.obj.toLowerCase().trim() === entite.toLowerCase().trim());

      if (entite === 'Inscription' || entite === 'Preinscription') {

        let piecesByTypeStructure: PiecesByTypeStructure;

        if (entite === 'Inscription') {
          piecesByTypeStructure = {
            Cogito: { label: this.translateService.instant('ged.entite.inscription_cogito'), pieces: [] },
            Diabolo: { label: this.translateService.instant('ged.entite.inscription_diabolo'), pieces: [] },
            Mikado: { label: this.translateService.instant('ged.entite.inscription_mikado'), pieces: [] },
          };
        } else {
          piecesByTypeStructure = {
            Cogito: { label: this.translateService.instant('ged.entite.preinscription_cogito'), pieces: [] },
            Mikado: { label: this.translateService.instant('ged.entite.preinscription_mikado'), pieces: [] },
            Lasido: { label: this.translateService.instant('ged.entite.preinscription_lasido'), pieces: [] }
          };
        }

        pieces.forEach(p => {
          if (p.typeStructure === "Cogito") {
            piecesByTypeStructure.Cogito.pieces.push(p);
          }
          if (p.typeStructure === "Mikado") {
            piecesByTypeStructure.Mikado.pieces.push(p);
          }
          if (p.typeStructure === "Diabolo" && entite === "Inscription") {
            piecesByTypeStructure.Diabolo.pieces.push(p);
          }
          if (p.typeStructure === "Lasido" && entite === "Preinscription") {
            piecesByTypeStructure.Lasido.pieces.push(p);
          }
        })

        piecesAFournirByEntite[entite] = { pieces, piecesByTypeStructure };

        if (entite === "Inscription") {
          this.sortByOrder(piecesAFournirByEntite[entite]?.piecesByTypeStructure?.Diabolo?.pieces);
        } else {
          this.sortByOrder(piecesAFournirByEntite[entite]?.piecesByTypeStructure?.Lasido?.pieces);
        }

        this.sortByOrder(piecesAFournirByEntite[entite]?.piecesByTypeStructure?.Cogito?.pieces);
        this.sortByOrder(piecesAFournirByEntite[entite]?.piecesByTypeStructure?.Mikado?.pieces);

      } else {

        piecesAFournirByEntite[entite] = { pieces };
        this.sortByOrder(piecesAFournirByEntite[entite]?.pieces);

      }
    })

    return piecesAFournirByEntite;
  }
}
